import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  TablePagination,
  Stack,
  Tooltip,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getSuppliers } from '../../services/supplierService';
import { Supplier } from '../../types/supplier';
import InfoTooltip from '../common/InfoTooltip';

interface SupplierListProps {
  onAddClick: () => void;
  onRowClick: (supplier: Supplier) => void;
}

const SupplierList: React.FC<SupplierListProps> = ({ onAddClick, onRowClick }) => {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchSuppliers = async () => {
      if (currentOrganization?.id) {
        try {
          const fetchedSuppliers = await getSuppliers(currentOrganization.id);
          setSuppliers(fetchedSuppliers);
        } catch (error) {
          console.error('Error fetching suppliers:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchSuppliers();
  }, [currentOrganization?.id]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredSuppliers = suppliers.filter((supplier) =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedSuppliers = filteredSuppliers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h5" component="h2">
            {t('suppliers.title')}
          </Typography>
          <InfoTooltip text={t('suppliers.info')} />
        </Stack>
        <Button
          variant="contained"
          color="primary"
          onClick={onAddClick}
        >
          {t('suppliers.add')}
        </Button>
      </Box>

      <TextField
        fullWidth
        margin="normal"
        label={t('suppliers.search')}
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('suppliers.form.name')}</TableCell>
              <TableCell>{t('suppliers.form.type')}</TableCell>
              <TableCell>{t('suppliers.form.contactName')}</TableCell>
              <TableCell>{t('suppliers.form.contactPhone')}</TableCell>
              <TableCell>{t('suppliers.form.email')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSuppliers.map((supplier) => (
              <Tooltip 
                key={supplier.id}
                title={t('suppliers.messages.clickForDetails')}
                placement="top"
              >
                <TableRow 
                  onClick={() => onRowClick(supplier)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '& td': {
                      borderBottom: 1,
                      borderColor: 'divider',
                    },
                    '&:hover td': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <TableCell>{supplier.name}</TableCell>
                  <TableCell>{t(`suppliers.type.${supplier.type}`)}</TableCell>
                  <TableCell>{supplier.contactName}</TableCell>
                  <TableCell>{supplier.contactPhone}</TableCell>
                  <TableCell>{supplier.email}</TableCell>
                </TableRow>
              </Tooltip>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredSuppliers.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t('common.rowsPerPage')}
      />
    </Box>
  );
};

export default SupplierList;
